import { useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  FormProvider,
  useForm,
  useWatch,
  type Resolver,
} from 'react-hook-form';
import { array, object, string, type InferType } from 'yup';

import { FormSelect } from '@/components/form/form-select';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { Client } from '@/features/console/console.types';
import { useCompanyOptions } from '@/features/console/pages/clients/clients.hook';
import { UserAccessService } from '@/features/user-access/user-access.service';
import type { IBranch, IRole } from '@/features/user-access/user-access.types';
import { useBranchList } from '@/hooks/use-branch-list';
import { useStartViewAs } from '../view-as.hooks';

const SEARCH_DEBOUNCE_MS = 400;

// The API refuses the super Admin role itself; a Company Admin sees every
// location of the company, so picking some would change nothing.
const SUPER_ADMIN_ROLE = 'Admin';
const COMPANY_ADMIN_ROLE = 'Company Admin';

const viewAsSchema = object({
  companyId: string().required('Pick a company'),
  roleId: string().required('Pick a role'),
  branchIds: array(string().required()).default([]),
});

type ViewAsInput = InferType<typeof viewAsSchema>;

function ViewAsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onClose={() => onOpenChange(false)}>
        <DialogHeader>
          <DialogTitle>View as role</DialogTitle>
          <DialogDescription>
            See the CMS the way a company role sees it. Read-only, for up to 60
            minutes.
          </DialogDescription>
        </DialogHeader>

        <ViewAsForm onCancel={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

function ViewAsForm({ onCancel }: { onCancel: () => void }) {
  const form = useForm<ViewAsInput>({
    resolver: yupResolver(viewAsSchema) as Resolver<ViewAsInput>,
    defaultValues: { companyId: '', roleId: '', branchIds: [] },
  });
  const startViewAs = useStartViewAs();

  const companyId = useWatch({ control: form.control, name: 'companyId' });
  const roleId = useWatch({ control: form.control, name: 'roleId' });

  // The company list is searched on the server, a page at a time; the picked
  // company stays an option while the next search shows others.
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<Client.IClient>();
  const pushQuery = useMemo(
    () => debounce((value: string) => setQuery(value), SEARCH_DEBOUNCE_MS),
    []
  );
  useEffect(() => () => pushQuery.cancel(), [pushQuery]);

  const { data: companies, isSearching } = useCompanyOptions(query);
  const companyOptions = useMemo(
    () =>
      picked && !companies.some(company => company.id === picked.id)
        ? [picked, ...companies]
        : companies,
    [companies, picked]
  );

  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: UserAccessService.getListRole,
    select: data => (data.data ?? []).filter(r => r.name !== SUPER_ADMIN_ROLE),
  });

  const isCompanyAdmin =
    roles.find(role => role.id === roleId)?.name === COMPANY_ADMIN_ROLE;
  const showsLocations = Boolean(companyId) && !isCompanyAdmin;

  const { branches, isLoading: isLoadingBranches } = useBranchList({
    enabled: showsLocations,
    companyId,
  });

  const onSubmit = (values: ViewAsInput) => {
    startViewAs.mutate({
      companyId: values.companyId,
      roleId: values.roleId,
      branchIds: isCompanyAdmin ? [] : values.branchIds,
    });
  };

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
      >
        <FormSelect<Client.IClient, false>
          name="companyId"
          label="Company"
          placeholder="Search a company"
          options={companyOptions}
          getOptionLabel={item => item.name ?? ''}
          getOptionValue={item => item.id ?? ''}
          optionKey="id"
          filterOption={null}
          isLoading={isSearching}
          onInputChange={(value, meta) => {
            if (meta.action === 'input-change') pushQuery(value);
          }}
          onChange={option => {
            setPicked(option ?? undefined);
            // Another company's locations.
            form.setValue('branchIds', []);
          }}
          required
        />

        <FormSelect<IRole, false>
          name="roleId"
          label="Role"
          placeholder="Select a role"
          options={roles}
          getOptionLabel={item => item.name ?? ''}
          getOptionValue={item => item.id ?? ''}
          optionKey="id"
          required
        />

        {showsLocations && (
          <FormSelect<IBranch, true>
            isMulti
            name="branchIds"
            label="Locations"
            placeholder="All locations"
            options={branches}
            getOptionLabel={item => item.name ?? ''}
            getOptionValue={item => item.id ?? ''}
            optionKey="id"
            isLoading={isLoadingBranches}
            closeMenuOnSelect={false}
            optional
          />
        )}

        <DialogFooter>
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={startViewAs.isPending}
            loading={startViewAs.isPending}
          >
            Start viewing
          </Button>
        </DialogFooter>
      </form>
    </FormProvider>
  );
}

export { ViewAsDialog };
